import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LogoWordmark from "@/components/LogoWordmark";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/industries", label: "Industries" },
    { to: "/pricing", label: "Pricing" },
    { to: "/team", label: "Team" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`glass-header sticky top-0 z-50 border-b transition-colors duration-300 ${
                scrolled ? "border-brand-ink/15" : "border-transparent"
            }`}
        >
            <div className="container-page flex items-center justify-between h-16 md:h-20">
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link" aria-label="Vittaniti home">
                    <LogoWordmark height={32} className="shrink-0" />
                    <span className="hidden md:flex flex-col leading-none border-l border-brand-border pl-3">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">Shared Solutions</span>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mt-0.5">Hyderabad · India</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
                    {NAV.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                                    isActive ? "text-brand-ink" : "text-brand-muted hover:text-brand-ink"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <span className="inline-flex items-center gap-2">
                                    {item.label}
                                    {isActive && <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-amber" />}
                                </span>
                            )}
                        </NavLink>
                    ))}
                    <Link to="/contact" data-testid="nav-cta" className="btn-primary ml-2">
                        Free Consultation
                    </Link>
                </nav>

                <button
                    onClick={() => setOpen(v => !v)}
                    className="md:hidden p-2 rounded-full text-brand-ink hover:bg-brand-creamLight"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-brand-ink/10 bg-brand-bg" data-testid="mobile-menu-panel">
                    <nav className="container-page py-4 flex flex-col gap-1" aria-label="Mobile">
                        {NAV.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-3 py-3 rounded-lg text-base ${
                                        isActive
                                            ? "bg-brand-creamLight text-brand-ink font-semibold"
                                            : "text-brand-ink/85 hover:bg-brand-creamLight/60"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <Link to="/contact" data-testid="mobile-nav-cta" className="btn-primary mt-3 w-full">
                            Free Consultation
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}
